import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  useEvent,
  useEventRsvps,
  useDuplicateEvent,
} from "../../lib/events";
import { useCan, useMe } from "../../lib/auth";
import { formatInTz } from "../../lib/tz";
import { humanizeRrule } from "../../lib/recurrence-humanize";
import { ApiError } from "../../lib/api";
import { EventActions } from "../calendar/EventActions";
import { RsvpPanel } from "../calendar/RsvpPanel";

/**
 * Shared event-detail body — used in BOTH the modal overlay and the page-version
 * route on BOTH the public and dashboard surfaces. The component itself owns the
 * auth/role gates (canEdit, canSeeRsvps, isOwn) so callers don't have to compute
 * them in two places. Surface-specific differences are limited to:
 *
 *  - `mode` ('page' vs 'modal') — affects type sizes and which footer chrome shows.
 *  - `basePath` — where back / Edit / View-full links go.
 *  - `newsBasePath` — where related-article Links resolve.
 *
 * Recurrence rendering uses `humanizeRrule()` from `lib/recurrence-humanize.ts`
 * — the only place in the app that turns RRULE strings into prose.
 */

export type EventBasePath = "/calendar" | "/dashboard/calendar";
export type NewsBasePath = "/news" | "/dashboard/articles";

interface EventViewProps {
  slug: string;
  mode: "page" | "modal";
  basePath: EventBasePath;
  newsBasePath: NewsBasePath;
}

export function EventView({ slug, mode, basePath, newsBasePath }: EventViewProps) {
  const navigate = useNavigate();
  const me = useMe();
  const eventQ = useEvent(slug);
  const ev = eventQ.data?.event;

  const canEditAny = useCan("events.edit_any");
  const canEditOwn = useCan("events.edit_own");
  const canCreate = useCan("events.create");

  const duplicate = useDuplicateEvent();
  const [showRsvps, setShowRsvps] = useState(false);

  const isOwn = !!me.data?.user && !!ev && ev.createdBy === me.data.user.id;
  const canEdit = (canEditOwn && isOwn) || canEditAny;
  const canSeeRsvps = canEdit;
  const rsvpsQ = useEventRsvps(canSeeRsvps && showRsvps ? ev?.id ?? null : null);

  if (eventQ.isLoading) {
    return <p className="font-display-italic text-[var(--color-ink-mute)]">Loading…</p>;
  }
  if (!ev) {
    return (
      <div>
        <h2 className="font-display text-[1.85rem] leading-tight mb-4">Not found.</h2>
        <Link to={basePath} className="btn-ghost">
          <span>← Calendar</span>
        </Link>
      </div>
    );
  }

  const onDuplicate = async () => {
    try {
      const res = await duplicate.mutateAsync(ev.id);
      navigate(`/dashboard/calendar/${res.event.slug}/edit`);
    } catch (err) {
      const e = err as ApiError;
      window.alert(e?.body?.message ?? e?.message ?? "Duplicate failed.");
    }
  };

  const isPage = mode === "page";

  return (
    <article className={isPage ? "event-view event-view--page" : "event-view event-view--modal"}>
      {/* Header — pageMode renders a hero head; modalMode lets the Modal's
          own header carry the title, so we only render the eyebrow + summary. */}
      {isPage && (
        <header className="mb-6">
          <p className="eyebrow mb-3">
            {ev.category.replace(/-/g, " ")}
            <span className="mx-2 text-[var(--color-ink-mute)]">·</span>
            <span className="text-[var(--color-ink-mute)]">{ev.locationType}</span>
            {ev.status !== "published" && (
              <>
                <span className="mx-2 text-[var(--color-ink-mute)]">·</span>
                <span className="text-[var(--color-violet)]">{ev.status}</span>
              </>
            )}
          </p>
          <h1 className="font-display text-[clamp(2.25rem,5.5vw,4rem)] leading-[1.04] tracking-[-0.02em]">
            {ev.title}
          </h1>
          {ev.summary && (
            <p className="font-display-italic text-[clamp(1.2rem,2.2vw,1.55rem)] text-[var(--color-ink-mute)] max-w-[50ch] mt-4">
              {ev.summary}
            </p>
          )}
        </header>
      )}

      {!isPage && ev.summary && (
        <p className="font-display-italic text-[1.05rem] leading-[1.5] text-[var(--color-ink-mute)] max-w-[55ch] mb-6">
          {ev.summary}
        </p>
      )}

      {ev.coverImage && (
        <div className={isPage ? "image-frame mb-12" : "image-frame mb-8"} style={{ aspectRatio: "16 / 9" }}>
          <img src={ev.coverImage} alt="" />
        </div>
      )}

      {isPage && <hr className="rule-violet w-12 mb-9" />}

      <dl className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-y-3 gap-x-6 mb-7">
        <Cell
          label="When"
          value={
            ev.allDay || ev.timeTbd
              ? formatInTz(ev.startsAt, ev.timezone, {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }) + (ev.timeTbd ? " · time to be announced" : "")
              : `${formatInTz(ev.startsAt, ev.timezone)} → ${formatInTz(ev.endsAt, ev.timezone, { hour: "2-digit", minute: "2-digit", hour12: false })} (${ev.timezone})`
          }
        />
        {ev.location && (
          <>
            <dt className="auth-label">Where</dt>
            <dd>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ev.location)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[var(--color-violet)]"
              >
                {ev.location}
              </a>
            </dd>
          </>
        )}
        {ev.onlineUrl && (
          <>
            <dt className="auth-label">Online</dt>
            <dd>
              <a className="article-prose" href={ev.onlineUrl} target="_blank" rel="noreferrer">
                {ev.onlineUrl}
              </a>
            </dd>
          </>
        )}
        {ev.capacity !== null && (
          <Cell
            label="Capacity"
            value={`${ev.rsvpCount} of ${ev.capacity} going${ev.isFull ? " (full)" : ""}`}
          />
        )}
        {ev.capacity === null && ev.rsvpCount > 0 && (
          <Cell label="Going" value={String(ev.rsvpCount)} />
        )}
        {ev.recurrenceRule && (
          <Cell label="Repeats" value={humanizeRrule(ev.recurrenceRule)} />
        )}
        {ev.authorName && <Cell label="Hosted by" value={ev.authorName} />}
      </dl>

      <EventActions event={ev} />

      {ev.description && (
        <div className="article-prose mt-8 mb-8 whitespace-pre-line">{ev.description}</div>
      )}

      {me.data?.user && ev.status === "published" && <RsvpPanel event={ev} />}

      {canSeeRsvps && (
        <section className="rsvp-list-section mt-8">
          <button
            type="button"
            className="btn-app-ghost"
            onClick={() => setShowRsvps((s) => !s)}
            aria-expanded={showRsvps}
          >
            <span>
              {showRsvps ? "Hide" : "Show"} attendees ({ev.rsvpCount + ev.maybeCount})
            </span>
          </button>
          {showRsvps && (
            <div className="rsvp-list mt-4">
              {rsvpsQ.isLoading && <p className="text-[var(--color-ink-mute)]">Loading…</p>}
              {rsvpsQ.data?.rsvps.length === 0 && (
                <p className="text-[var(--color-ink-mute)]">No RSVPs yet.</p>
              )}
              {(rsvpsQ.data?.rsvps ?? []).map((r) => (
                <div key={r.userId} className="rsvp-row">
                  <span className={`rsvp-status is-${r.status}`}>{r.status}</span>
                  <span className="rsvp-name">{r.displayName}</span>
                  {r.note && <span className="rsvp-note">"{r.note}"</span>}
                  <span className="rsvp-when">{format(new Date(r.updatedAt), "d LLL · HH:mm")}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {ev.relatedDocuments.length > 0 && (
        <section className="mt-12">
          <h2 className={isPage ? "font-display text-[1.7rem] tracking-tight mb-7" : "font-display text-[1.4rem] tracking-tight mb-5"}>
            Materials for this event
          </h2>
          <p className="font-grotesk text-[0.78rem] text-[var(--color-ink-soft)] -mt-5 mb-5 max-w-[60ch]">
            Texts, recordings, and study guides linked to this event — open
            the document to find PDFs in your script of choice and the audio.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {ev.relatedDocuments.map((d) => (
              <li key={d.id}>
                <Link
                  to={`/library/${d.slug}`}
                  className="veda-panel transition-colors"
                >
                  {d.coverImage && (
                    <img
                      src={d.coverImage}
                      alt=""
                      className="w-full h-24 object-cover mb-3"
                      loading="lazy"
                    />
                  )}
                  <p className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-violet)] mb-1.5">
                    Document
                  </p>
                  <p className="font-display text-[1.15rem] leading-[1.2] tracking-tight">
                    {d.title}
                  </p>
                  {d.subtitle && (
                    <p className="font-display-italic text-[0.95rem] text-[var(--color-ink-mute)] mt-1">
                      {d.subtitle}
                    </p>
                  )}
                  {d.excerpt && (
                    <p className="text-[0.85rem] leading-[1.5] text-[var(--color-ink-soft)] mt-1.5">
                      {d.excerpt.length > 140 ? d.excerpt.slice(0, 140).trimEnd() + "…" : d.excerpt}
                    </p>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {ev.relatedArticles.length > 0 && (
        <section className="mt-12">
          <h2 className={isPage ? "font-display text-[1.7rem] tracking-tight mb-7" : "font-display text-[1.4rem] tracking-tight mb-5"}>
            Articles about this event
          </h2>
          <ul className="flex flex-col">
            {ev.relatedArticles.map((a, idx) => (
              <li
                key={a.id}
                className="py-4 border-t border-[var(--color-rule)]"
                style={idx === 0 ? { borderTop: "none", paddingTop: 0 } : undefined}
              >
                <p className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-violet)] mb-1.5">
                  {a.category.replace(/-/g, " ")}
                  {a.publishedAt && (
                    <>
                      <span className="mx-2 text-[var(--color-ink-mute)]">·</span>
                      <span className="text-[var(--color-ink-mute)]">
                        {format(new Date(a.publishedAt), "d LLL yyyy")}
                      </span>
                    </>
                  )}
                </p>
                <Link
                  to={`${newsBasePath}/${a.slug}`}
                  className="font-display text-[1.3rem] leading-[1.2] tracking-[-0.01em] hover:text-[var(--color-violet)]"
                >
                  {a.title}
                </Link>
                {a.excerpt && (
                  <p className="text-[0.95rem] leading-[1.55] text-[var(--color-ink-soft)] mt-2 max-w-[60ch]">
                    {a.excerpt}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Footer chrome — page mode shows back link + sign-in CTA; modal mode
          relies on the Modal's headerActions slot for Edit / View full. */}
      {isPage ? (
        <div className="flex flex-wrap gap-3 mt-10 pt-8 border-t border-[var(--color-rule)]">
          <Link to={basePath} className="btn-ghost">
            <span>← Calendar</span>
          </Link>
          {canCreate && (
            <button type="button" className="btn-ghost" onClick={onDuplicate}>
              <span>Duplicate</span>
            </button>
          )}
          {canEdit && (
            <Link to={`/dashboard/calendar/${ev.slug}/edit`} className="btn-primary">
              <span>Edit event</span>
              <span className="btn-arrow-icon" aria-hidden>→</span>
            </Link>
          )}
          {!me.data?.user && ev.status === "published" && (
            <Link to={`/login?next=/calendar/${ev.slug}`} className="btn-primary">
              <span>Sign in to RSVP</span>
              <span className="btn-arrow-icon" aria-hidden>→</span>
            </Link>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap gap-3 mt-8 pt-6 border-t border-[var(--color-rule)] event-view-modal-footer">
          {canCreate && (
            <button type="button" className="btn-ghost" onClick={onDuplicate}>
              <span>Duplicate</span>
            </button>
          )}
          {canEdit && (
            <Link to={`/dashboard/calendar/${ev.slug}/edit`} className="btn-ghost">
              <span>Edit event</span>
              <span className="btn-arrow-icon" aria-hidden>→</span>
            </Link>
          )}
          <Link to={`${basePath}/${ev.slug}`} className="btn-ghost">
            <span>View full page</span>
            <span className="btn-arrow-icon" aria-hidden>→</span>
          </Link>
          {!me.data?.user && ev.status === "published" && (
            <Link to={`/login?next=${basePath}/${ev.slug}`} className="btn-primary">
              <span>Sign in to RSVP</span>
              <span className="btn-arrow-icon" aria-hidden>→</span>
            </Link>
          )}
        </div>
      )}
    </article>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="auth-label">{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

/**
 * Compose the modal header eyebrow string (category · type · status) from a
 * partial event. Exported so EventModalRoute can pass it to the Modal's
 * `eyebrow` prop while the body still renders inside.
 */
export function composeEventEyebrow(ev: {
  category: string;
  locationType: string;
  status: string;
}): string {
  const parts = [
    ev.category.replace(/-/g, " "),
    ev.locationType,
  ];
  if (ev.status !== "published") parts.push(ev.status);
  return parts.join(" · ");
}
